import RemoteCase from '../case/remote-case';
import constants from '../constants';
import ContextProcessor from '../context/context-processor';
import Debugger from '../debug/debugger';
import type { ConfigurationType } from '../initialiser/configurations';
import type { ActionObject } from '../globals';

type SpecialChars = {
  SLASH: '/';
};

type CoreHeaders = {
  debugInfo?: {
    debugId?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
};

type Headers = {
  [key: string]: string | undefined | null;
};

/**
 * Provides APIs for adding headers to requests invoked from Pega service broker infra.
 * These APIs can be used for different purposes such as adding Headers, unregistering them and getting all the registered headers.
 * @module HeaderProcessor
 */
class HeaderProcessorClass {
  private externalHeaders: Headers;
  private defaultHeaders: { [name: string]: string | null };
  private SPECIAL_CHARS: SpecialChars;

  constructor() {
    this.externalHeaders = {};
    this.defaultHeaders = {};
    /**
     * API to return coreHeaders from redux store
     * @param context - the ID of the container item that provides the current context of the case
     * @returns returns coreHeaders
     */
    this.SPECIAL_CHARS = {
      SLASH: '/'
    };
  }

  /**
   * This api is used to register external headers to the service broker fetch calls
   * @param name name of the header that needs to be registered
   * @param value value corresponding to the header name provided
   * @returns returns false if a default header already exists by name else true in case of successfull registration
   * @example <caption>Example for registerHeader api</caption>
   * PCore.getRestClient().getHeaderProcessor().registerHeader(key, value)
   */
  registerHeader(name: string, value?: string) {
    if (!this.defaultHeaders[name]) {
      this.externalHeaders[name] = value;
      return true;
    }
    return false;
  }

  /**
   * This api is used to de-register external headers
   * @param name header name to be de-registered
   * @example <caption>Example for unRegisterHeader api</caption>
   * PCore.getRestClient().getHeaderProcessor().unRegisterHeader(key)
   */
  unRegisterHeader(name: string) {
    if (this.externalHeaders[name]) {
      delete this.externalHeaders[name];
    }
  }

  /**
   * This spi is used to return all the registered headers in form of a Json object
   * @returns an object containing all the headers and its value as a Json object
   * @example <caption>Example for getRegisteredHeaders api</caption>
   * PCore.getRestClient().getHeaderProcessor().getRegisteredHeaders()
   */
  getRegisteredHeaders() {
    return this.externalHeaders;
  }

  setDefaultHeaders(headersObj: ConfigurationType['additionalHeaders']) {
    this.defaultHeaders = {
      ...this.defaultHeaders,
      ...headersObj
    };
  }

  getDefaultHeaders() {
    return this.defaultHeaders;
  }

  /**
   * add debug headers to fetch API calls
   * @param debugInfo - Contains debugId
   * @returns headers
   */
  addDebugHeaders(debugInfo: { debugId?: string; [key: string]: unknown } = {}) {
    return {
      [constants.COREHEADER_APP_DEBUG_ID]: debugInfo.debugId,
      [constants.COREHEADER_APP_DEBUG_LEVELS]: Debugger.getDebugLevels()
    };
  }

  getCoreHeadersFromContext(context: string): CoreHeaders | undefined {
    if (['root', 'app'].includes(context) || !context) return;
    const coreHeaders = ContextProcessor.getProperty(constants.COREHEADERS, '', context);
    if (coreHeaders) {
      return {
        ...coreHeaders,
        debugInfo: Debugger.getPreviewMode() && {
          ...coreHeaders.debugInfo,
          [constants.COREHEADER_APP_DEBUG_LEVELS]: Debugger.getDebugLevels()
        }
      };
    }
    context = context.substr(0, context.lastIndexOf(this.SPECIAL_CHARS.SLASH));
    return this.getCoreHeadersFromContext(context);
  }

  processCoreHeaders(headers: Headers, payload: unknown, context: string) {
    const coreHeaders = this.getCoreHeadersFromContext(context);
    if (coreHeaders) {
      const headerKeys = Object.keys(headers);
      if (headerKeys.includes(constants.SKIPCOREHEADERS)) {
        delete headers[constants.SKIPCOREHEADERS];
        return;
      }
      const coreHeadersKeys = Object.assign({}, ...Object.values(coreHeaders));
      Object.keys({ ...coreHeadersKeys }).forEach((header) => {
        if (!headerKeys.includes(header)) {
          headers[header] = coreHeadersKeys[header];
        }
      });
    }
  }

  injectCoreAdditionalHeaders(
    headers: NonNullable<ActionObject['headers']>,
    payload: { caseTypeID: string },
    context: string,
    options: { includeRemoteSystemIdIfPresent?: boolean }
  ) {
    if (options?.includeRemoteSystemIdIfPresent) {
      RemoteCase.injectRemoteCaseHeader(headers, payload, context);
    }
    this.processCoreHeaders(headers, payload, context);
  }
}

const HeaderProcessor = new HeaderProcessorClass();
export default HeaderProcessor;
